import { useMemo } from 'react';

import DecisionQueue from './decision-queue';
import RunningWork from './running-work';
import Button from '../../../common/components/button/button';
import EmptyState from '../../../common/components/state-message/empty-state';
import ErrorState from '../../../common/components/state-message/error-state';
import LoadingState from '../../../common/components/state-message/loading-state';
import Section from '../../../common/components/section/section';
import Timeline from '../../../common/components/timeline/timeline';
import { deriveAgentStatus } from '../../../common/package/seat';
import { AGENT_STATUS, EVENT_KIND_TONE } from '../../../core/constants/domain';
import { ROUTES } from '../../../core/constants/routes';
import { partOfDay } from '../../../core/utils/date';
import { useProjectCatalog } from '../../../global/contexts/catalogs/catalogs';
import { useAttention, useSeats } from '../../../global/contexts/coordination/coordination';
import { useEvents, useTasksList } from '../../../global/contexts/work/work';
import { decisionHeadline, sortByAttention } from '../utils/summary';

import '../styles/overview.css';

export default function HomeOverview() {
    const reviewsQuery = useTasksList({ workflowState: 'in_review' });
    const claimedQuery = useTasksList({ workflowState: 'claimed', limit: 1 });
    const blockedQuery = useTasksList({ workflowState: 'blocked', limit: 1 });
    const attentionQuery = useAttention();
    const seatsQuery = useSeats();
    const eventsQuery = useEvents({ limit: 8 });
    const { data: projects } = useProjectCatalog();

    const reviews = reviewsQuery.data?.items ?? [];
    const attention = attentionQuery.data?.items ?? [];
    const decisionCount = reviews.length + attention.length;

    const activeSeats = useMemo(() => sortByAttention(
        (seatsQuery.data?.items ?? [])
            .map((seat) => ({ seat, status: deriveAgentStatus(seat) }))
            .filter(({ status }) => status === AGENT_STATUS.RUNNING || status === AGENT_STATUS.BLOCKED),
    ), [seatsQuery.data]);

    const activity = (eventsQuery.data?.items ?? []).map((event) => ({
        id: event.id,
        at: event.at,
        label: event.kindLabel,
        title: event.summary,
        actor: event.actor,
        tone: EVENT_KIND_TONE[event.kind],
        to: event.taskId ? ROUTES.TASK(event.taskId) : null,
    }));

    const isDecisionLoading = reviewsQuery.isPending || attentionQuery.isPending;

    return (
        <div className="home-overview">
            <header className="home-overview__hero">
                <p className="home-overview__greeting">{partOfDay()}.</p>
                <h1 className="home-overview__headline">
                    {isDecisionLoading ? 'Revisando qué te espera…' : decisionHeadline(decisionCount)}
                </h1>
                <dl className="home-overview__stats">
                    <div className="home-overview__stat">
                        <dt>En revisión</dt>
                        <dd>{reviewsQuery.data?.total ?? '—'}</dd>
                    </div>
                    <div className="home-overview__stat">
                        <dt>En progreso</dt>
                        <dd>{claimedQuery.data?.total ?? '—'}</dd>
                    </div>
                    <div className="home-overview__stat home-overview__stat--alert">
                        <dt>Bloqueadas</dt>
                        <dd>{blockedQuery.data?.total ?? '—'}</dd>
                    </div>
                </dl>
            </header>

            <div className="home-overview__grid">
                <Section
                    title="Necesita tu decisión"
                    count={isDecisionLoading ? undefined : decisionCount}
                    className="home-overview__decisions"
                    action={<Button variant="ghost" size="small" to={ROUTES.REVIEWS}>Ver revisiones</Button>}
                >
                    {isDecisionLoading && <LoadingState label="Cargando decisiones pendientes" rows={3} />}
                    {reviewsQuery.isError && <ErrorState error={reviewsQuery.error} onRetry={reviewsQuery.refetch} />}
                    {!isDecisionLoading && !reviewsQuery.isError && decisionCount === 0 && (
                        <EmptyState title="Estás al día" description="No hay revisiones abiertas ni avisos vigentes." />
                    )}
                    {!isDecisionLoading && decisionCount > 0 && (
                        <DecisionQueue reviews={reviews} attention={attention} projects={projects} />
                    )}
                </Section>

                <Section
                    title="Trabajo en curso"
                    count={seatsQuery.isPending ? undefined : activeSeats.length}
                    className="home-overview__running"
                    action={<Button variant="ghost" size="small" to={ROUTES.TEAMS}>Ver equipos</Button>}
                >
                    {seatsQuery.isPending && <LoadingState label="Cargando agentes" rows={2} />}
                    {seatsQuery.isError && <ErrorState error={seatsQuery.error} onRetry={seatsQuery.refetch} />}
                    {seatsQuery.isSuccess && activeSeats.length === 0 && (
                        <EmptyState title="Ningún agente está ejecutando" description="Los puestos conectados esperan trabajo." />
                    )}
                    {activeSeats.length > 0 && <RunningWork seats={activeSeats} />}
                </Section>

                <Section title="Actividad reciente" className="home-overview__activity">
                    {eventsQuery.isPending && <LoadingState label="Cargando actividad" rows={4} />}
                    {eventsQuery.isError && <ErrorState error={eventsQuery.error} onRetry={eventsQuery.refetch} />}
                    {eventsQuery.isSuccess && activity.length === 0 && <EmptyState title="Sin actividad reciente" />}
                    {activity.length > 0 && <Timeline items={activity} label="Actividad reciente" />}
                </Section>
            </div>
        </div>
    );
}
